import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import type { AskUserFeedbackAnswer } from '@renderer/chat/integrations/ask-user-feedback'
import { STAGED_ACTIONS_QUERY_KEY, type StagedAction } from './use-staged-actions'
import { useStagedActionEndpoints } from './staged-actions/endpoints'
import { useResumeChatAfterInvoke } from './staged-actions/resume-chat'
import { answerableResumeLabel } from '../views/chat-output-view/chat-output'

/**
 * Submits a pending background AskUserFeedback staged action via
 * `POST /v1/staged-actions/:id/feedback`. The backend stores the completions,
 * resolves the action, and fires its `resume_chat` hook so the run that asked
 * continues and saves what it learned.
 *
 * Skipping posts here too (`dismissed: true`): the run is paused on this card
 * either way, and only the resume can tell it to record nothing and stop.
 * Mirrors `use-answer-question-action.ts` — separate because the payload shape
 * and the endpoint differ.
 */
export function useAnswerFeedbackAction() {
  const { resolveAnswerableStagedAction } = useStagedActionEndpoints()
  const queryClient = useQueryClient()
  const resumeChatAfterAnswer = useResumeChatAfterInvoke()

  const mutation = useMutation({
    mutationFn: ({ actionId, answer }: { actionId: string; answer: AskUserFeedbackAnswer }) =>
      resolveAnswerableStagedAction(actionId, 'feedback', answer),
    onSuccess: (resolvedAction, { actionId, answer }) => {
      // Reflect the answer in the cache before the refetch lands, for the same
      // two reasons as the question flow: `getActionStatus` reads any non-null
      // `outputData` as completed (so the card leaves the pending panel), and
      // `FeedbackActionContent` derives `existingResult` from it (so reopening
      // the drawer early renders read-only and can't post twice).
      const now = new Date().toISOString()
      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) =>
          old?.map((a) =>
            a.id === actionId
              ? { ...a, outputData: answer as unknown as StagedAction['outputData'], resolvedAt: a.resolvedAt ?? now }
              : a,
          ) ?? old,
      )

      // Show the answer summary until the backend's resumed turn arrives.
      resumeChatAfterAnswer(resolvedAction, {
        label: resolvedAction ? (answerableResumeLabel(resolvedAction) ?? undefined) : undefined,
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
    },
  })

  // Stable callback that throws on failure, matching `AskUserFeedbackCard`'s
  // `onSubmit` contract (it stays submittable for retry when onSubmit throws).
  const answerFeedback = useCallback(
    async (actionId: string, answer: AskUserFeedbackAnswer): Promise<void> => {
      await mutation.mutateAsync({ actionId, answer })
    },
    [mutation],
  )

  return { answerFeedback, isAnswering: mutation.isPending }
}
